import React, { useState } from 'react';

import { Button, Container, Input } from '@meu-mapa-nev/design-system';

import { AlertComFoco } from '../AlertComFoco.js';
import { ResumoDeErros, type ErroDescrito, type FieldError } from './ResumoDeErros.js';

export type EtapaAutenticacao = 'login' | 'cadastro' | 'recuperar-pedido' | 'recuperar-confirmar';

export interface TelaDeAutenticacaoProps {
  readonly etapa: EtapaAutenticacao;
  readonly onLogin: (email: string, password: string) => void;
  readonly onSignup: (email: string, password: string) => void;
  readonly onGoogle: () => void;
  readonly onRequestRecovery: (email: string) => void;
  readonly onCompleteRecovery: (password: string) => void;
  readonly onNavigate: (etapa: EtapaAutenticacao) => void;
  readonly submitting?: boolean;
  readonly localErrors?: readonly FieldError[];
  readonly aviso?: { type: 'info' | 'warning' | 'critical'; text: string } | undefined;
  /** Espaço onde o desafio antibot é desenhado. Ausente em `recuperar-confirmar`. */
  readonly desafioAntibot?: React.ReactNode;
}

const CAMPO_EMAIL = 'conta-campo-email';
const CAMPO_SENHA = 'conta-campo-senha';
const CAMPO_SENHA_NOVA = 'conta-campo-senha-nova';

const SENHA_MIN = 15;

function descreverErro(error: FieldError): ErroDescrito {
  switch (error.field) {
    case 'email':
      return {
        label: 'E-mail',
        message:
          error.code === 'invalid_format' ? 'está em formato inválido' : 'precisa ser preenchido',
        fieldId: CAMPO_EMAIL,
      };
    case 'password':
      return {
        label: 'Senha',
        message:
          error.code === 'too_short'
            ? `precisa ter pelo menos ${String(SENHA_MIN)} caracteres`
            : 'precisa ser preenchida',
        fieldId: CAMPO_SENHA,
      };
    case 'new_password':
      return {
        label: 'Nova senha',
        message:
          error.code === 'too_short'
            ? `precisa ter pelo menos ${String(SENHA_MIN)} caracteres`
            : 'precisa ser preenchida',
        fieldId: CAMPO_SENHA_NOVA,
      };
    case 'turnstile_token':
      return {
        label: 'Verificação de segurança',
        message: 'expirou ou não foi concluída. Recarregue a página e tente de novo',
      };
    default:
      return {
        label: 'Dados enviados',
        message: 'não foram aceitos. Se acontecer de novo, procure o atendimento',
      };
  }
}

const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TITULO: Record<EtapaAutenticacao, string> = {
  login: 'Entrar no Meu Mapa',
  cadastro: 'Criar sua conta',
  'recuperar-pedido': 'Recuperar acesso',
  'recuperar-confirmar': 'Definir uma nova senha',
};

/**
 * Autenticação por conta: login, cadastro e recuperação de senha por
 * e-mail/senha, com entrada adicional pelo Google.
 *
 * Substitui `acesso/TelaDeAcesso.tsx` (OTP por turma) — decisão explícita do
 * responsável pelo projeto em 11/09/2026: a nova experiência de conta
 * substitui o fluxo antigo, os dois não convivem.
 */
export const TelaDeAutenticacao: React.FC<TelaDeAutenticacaoProps> = ({
  etapa,
  onLogin,
  onSignup,
  onGoogle,
  onRequestRecovery,
  onCompleteRecovery,
  onNavigate,
  submitting = false,
  localErrors = [],
  aviso,
  desafioAntibot,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validation, setValidation] = useState<readonly FieldError[]>([]);

  const errors = validation.length > 0 ? validation : localErrors;
  const erroDoCampo = (field: string): string | undefined =>
    errors.some((e) => e.field === field) ? 'Confira este campo.' : undefined;

  const validarEmail = (): boolean => {
    if (!EMAIL_VALIDO.test(email)) {
      setValidation([{ field: 'email', code: 'invalid_format' }]);
      return false;
    }
    return true;
  };

  const submitLogin = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!validarEmail()) {
      return;
    }
    if (password.length === 0) {
      setValidation([{ field: 'password', code: 'required' }]);
      return;
    }
    setValidation([]);
    onLogin(email, password);
  };

  const submitCadastro = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!validarEmail()) {
      return;
    }
    if (password.length < SENHA_MIN) {
      setValidation([{ field: 'password', code: 'too_short' }]);
      return;
    }
    setValidation([]);
    onSignup(email, password);
  };

  const submitRecuperarPedido = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!validarEmail()) {
      return;
    }
    setValidation([]);
    onRequestRecovery(email);
  };

  const submitRecuperarConfirmar = (event: React.FormEvent): void => {
    event.preventDefault();
    if (password.length < SENHA_MIN) {
      setValidation([{ field: 'new_password', code: 'too_short' }]);
      return;
    }
    setValidation([]);
    onCompleteRecovery(password);
  };

  return (
    <Container size="reading" style={{ maxWidth: 'var(--nev-login-max)' }}>
      <h2
        style={{
          fontFamily: 'var(--nev-font-editorial)',
          fontSize: '1.5rem',
          fontWeight: 400,
          lineHeight: 1.3,
          color: 'var(--nev-text-primary)',
          marginBottom: 'var(--nev-space-4)',
        }}
      >
        {TITULO[etapa]}
      </h2>

      {aviso !== undefined && <AlertComFoco type={aviso.type}>{aviso.text}</AlertComFoco>}

      <ResumoDeErros errors={errors} describe={descreverErro} />

      {(etapa === 'login' || etapa === 'cadastro') && (
        <>
          <form onSubmit={etapa === 'login' ? submitLogin : submitCadastro} noValidate>
            <Input
              id={CAMPO_EMAIL}
              label="Seu e-mail"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              required
              disabled={submitting}
              error={erroDoCampo('email')}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
            <Input
              id={CAMPO_SENHA}
              label="Senha"
              type="password"
              autoComplete={etapa === 'login' ? 'current-password' : 'new-password'}
              value={password}
              required
              disabled={submitting}
              error={erroDoCampo('password')}
              helperText={
                etapa === 'cadastro' ? `Use pelo menos ${String(SENHA_MIN)} caracteres.` : undefined
              }
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />

            {desafioAntibot !== undefined && (
              <div style={{ marginBottom: 'var(--nev-space-4)' }}>{desafioAntibot}</div>
            )}

            <Button type="submit" variant="primary" fullWidth disabled={submitting}>
              {submitting ? 'Enviando…' : etapa === 'login' ? 'Entrar' : 'Criar minha conta'}
            </Button>
          </form>

          <div style={{ margin: 'var(--nev-space-4) 0' }}>
            <Button
              type="button"
              variant="outline"
              fullWidth
              disabled={submitting}
              onClick={onGoogle}
            >
              Continuar com o Google
            </Button>
          </div>

          {etapa === 'login' ? (
            <>
              <Button
                type="button"
                variant="outline"
                style={{
                  border: 0,
                  padding: '8px 0',
                  textDecoration: 'underline',
                  fontFamily: 'var(--nev-font-body)',
                  fontWeight: 400,
                }}
                disabled={submitting}
                onClick={() => {
                  onNavigate('recuperar-pedido');
                }}
              >
                Esqueci minha senha
              </Button>
              <div style={{ marginTop: 'var(--nev-space-3)', textAlign: 'center' }}>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => {
                    onNavigate('cadastro');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--nev-text-primary)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontFamily: 'var(--nev-font-body)',
                    fontSize: '0.9375rem',
                  }}
                >
                  Ainda não tenho conta
                </button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                disabled={submitting}
                onClick={() => {
                  onNavigate('login');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--nev-text-primary)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontFamily: 'var(--nev-font-body)',
                  fontSize: '0.9375rem',
                }}
              >
                Já tenho conta
              </button>
            </div>
          )}
        </>
      )}

      {etapa === 'recuperar-pedido' && (
        <form onSubmit={submitRecuperarPedido} noValidate>
          <Input
            id={CAMPO_EMAIL}
            label="Seu e-mail"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            required
            disabled={submitting}
            error={erroDoCampo('email')}
            helperText="Se este endereço tiver uma conta, enviaremos um link de recuperação."
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />

          {desafioAntibot !== undefined && (
            <div style={{ marginBottom: 'var(--nev-space-4)' }}>{desafioAntibot}</div>
          )}

          <Button type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Enviando…' : 'Enviar link de recuperação'}
          </Button>

          <div style={{ marginTop: 'var(--nev-space-3)' }}>
            <Button
              type="button"
              variant="outline"
              fullWidth
              disabled={submitting}
              onClick={() => {
                onNavigate('login');
              }}
            >
              Voltar para o login
            </Button>
          </div>
        </form>
      )}

      {etapa === 'recuperar-confirmar' && (
        <form onSubmit={submitRecuperarConfirmar} noValidate>
          <Input
            id={CAMPO_SENHA_NOVA}
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            value={password}
            required
            disabled={submitting}
            error={erroDoCampo('new_password')}
            helperText={`Use pelo menos ${String(SENHA_MIN)} caracteres.`}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />

          <Button type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Salvando…' : 'Definir nova senha'}
          </Button>
        </form>
      )}
    </Container>
  );
};
